import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { LEASE } from '@makemydeal/dr-platform-types';
import * as types from '../types/actionTypes';
import { getFeatureToggle, getOfferDetails } from '../selectors/offerNodeSelectors';

/**
 * DE771060: `backfillEquityAllocationOrder` (penciledInputsStateUtils.ts, US1997527) derives the
 * dealer's cash-priority order from apply-method fields a payment response reveals, but only stores it
 * for the *next* request — the response that taught the client the value was itself priced without it.
 * This middleware closes that gap: the moment the backfill transitions a lease term from no known order
 * to a real one, it fires one silent recalculation so the now-known order gets applied before anything
 * is shown or saved. Self-terminating: once `equityAllocationMethod` is set, the pre-condition below is
 * false on every later PAYMENT_RECEIVED, so this never fires twice for the same deal/session. Gated by
 * `DE771060_Enable_Equity_Allocation_Backfill_Recalc`; off reproduces current behavior exactly.
 */
const resolvePaymentReceivedOfferType = (action: AnyFSA): string | undefined =>
    action.meta?.offerType ?? action.meta?.middleware?.offerType;

const readLeaseEquityAllocationMethod = (state: any): string | undefined => {
    const method = getOfferDetails(state, LEASE)?.equityAllocationMethod;
    return typeof method === 'string' && method !== '' ? method : undefined;
};

const isBackfillRecalcEnabled = (state: any): boolean =>
    getFeatureToggle(state, 'DE771060_Enable_Equity_Allocation_Backfill_Recalc');

export const equityAllocationBackfillMiddleware = (store: any) => (next: any) => (action: AnyFSA) => {
    const isLeasePaymentReceived = action.type === types.PAYMENT_RECEIVED && resolvePaymentReceivedOfferType(action) === LEASE;
    const stateBeforeResponse = isLeasePaymentReceived ? store.getState() : undefined;
    const shouldWatchForBackfill =
        isLeasePaymentReceived &&
        isBackfillRecalcEnabled(stateBeforeResponse) &&
        !readLeaseEquityAllocationMethod(stateBeforeResponse);

    next(action);

    if (!shouldWatchForBackfill) return;

    const backfilledMethod = readLeaseEquityAllocationMethod(store.getState());
    if (!backfilledMethod) return;

    store.dispatch({
        type: types.EQUITY_ALLOCATION_METHOD_BACKFILLED,
        meta: {
            middleware: { payment: true, offerType: LEASE },
            ...(action.meta.originalAction?.meta?.rebaselineUnsavedChangesWhenClean && { rebaselineUnsavedChangesWhenClean: true })
        }
    });
};
